import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import {ArrowRight, MessageSquare, TrendingUp, Star, Loader2} from 'lucide-react'
import { useBlogPosts } from '../hooks/useBlogPosts'
import { useReviews } from '../hooks/useReviews'

export default function BlogPage() {
  const { posts, loading: postsLoading, fetchPosts } = useBlogPosts()
  const { reviews, loading: reviewsLoading, fetchReviews } = useReviews()

  useEffect(() => {
    fetchPosts({ filter: { category: 'Community Consensus' } })
    fetchReviews({ limit: 6 })
  }, [])

  const featuredPost = posts.find(p => p.featured)
  const otherPosts = posts.filter(p => !p.featured)

  const sentimentColor = (sentiment: string) => {
    if (sentiment === 'Positive') return 'text-green-600 bg-green-50'
    if (sentiment === 'Negative') return 'text-red-600 bg-red-50'
    return 'text-[#D97706] bg-[#F59E0B]/10'
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-16 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-[#3E2723] mb-6">
            Community Consensus Reviews
          </h1>
          <p className="text-xl text-[#3E2723]/70 max-w-3xl mx-auto leading-relaxed">
            Real opinions from whiskey enthusiasts. We aggregate sentiment from Reddit, Breaking Bourbon, and community forums—no marketing fluff.
          </p>
        </div>

        {postsLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-10 h-10 text-[#D97706] animate-spin" />
          </div>
        ) : (
          <>
            {/* Featured Post */}
            {featuredPost ? (
              <div className="mb-16 bg-white rounded-2xl shadow-2xl overflow-hidden border border-[#B87333]/20">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="h-96 bg-gradient-to-br from-[#D97706] via-[#B87333] to-[#3E2723] relative">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <MessageSquare className="w-32 h-32 text-white/20" />
                    </div>
                    <div className="absolute top-6 left-6 bg-[#F59E0B] text-[#3E2723] px-4 py-2 rounded-full text-sm font-bold">
                      Featured Review
                    </div>
                  </div>
                  <div className="p-12 flex flex-col justify-center">
                    <div className="flex flex-wrap gap-2 mb-4">
                      {featuredPost.tags?.map((tag, i) => (
                        <span key={i} className="bg-[#F59E0B]/10 text-[#D97706] px-3 py-1 rounded-full text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h2 className="text-3xl font-bold text-[#3E2723] mb-3">
                      {featuredPost.title}
                    </h2>
                    <p className="text-[#D97706] font-medium mb-4">{featuredPost.subtitle}</p>
                    <p className="text-[#3E2723]/70 leading-relaxed mb-6">
                      {featuredPost.excerpt}
                    </p>
                    <Link to={`/reviews/${featuredPost.slug}`} className="inline-flex items-center space-x-2 text-[#D97706] hover:text-[#B87333] font-bold group">
                      <span>Read Full Analysis</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Other Posts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
              {otherPosts.map((post) => (
                <div key={post._id} className="bg-white rounded-xl shadow-lg overflow-hidden border border-[#B87333]/10 hover:border-[#B87333]/30 transition-all hover:shadow-xl group">
                  <div className="h-48 bg-gradient-to-br from-[#3E2723] to-[#4E342E] relative">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <TrendingUp className="w-16 h-16 text-[#F59E0B]/30" />
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {post.tags?.map((tag, i) => (
                        <span key={i} className="bg-[#F59E0B]/10 text-[#D97706] px-2 py-1 rounded-full text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="text-xl font-bold text-[#3E2723] mb-2 group-hover:text-[#D97706] transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-[#D97706] font-medium text-sm mb-3">{post.subtitle}</p>
                    <p className="text-[#3E2723]/70 text-sm leading-relaxed mb-4">
                      {post.excerpt}
                    </p>
                    <Link to={`/reviews/${post.slug}`} className="inline-flex items-center space-x-1 text-[#D97706] hover:text-[#B87333] font-medium text-sm group">
                      <span>Read More</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Community Ratings Snapshot */}
        <div className="bg-[#3E2723] rounded-2xl p-10 lg:p-14">
          <h2 className="text-3xl font-bold text-[#FAF8F5] mb-2">Latest Community Ratings</h2>
          <p className="text-[#FAF8F5]/60 mb-10">Real quotes and scores pulled straight from Reddit, Breaking Bourbon, and forum threads.</p>

          {reviewsLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-8 h-8 text-[#F59E0B] animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review) => (
                <div key={review._id} className="bg-[#4E342E] rounded-xl p-6 border border-[#B87333]/20">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${sentimentColor(review.sentiment)}`}>
                      {review.sentiment}
                    </span>
                    <div className="flex items-center space-x-1 text-[#F59E0B]">
                      <Star className="w-4 h-4 fill-current" />
                      <span className="text-sm font-bold">{review.rating?.toFixed(1)}/10</span>
                    </div>
                  </div>
                  <p className="text-[#F59E0B] font-semibold text-sm mb-2">{review.bottleName}</p>
                  <p className="text-[#FAF8F5]/80 text-sm leading-relaxed mb-4 italic">
                    "{review.quote}"
                  </p>
                  <p className="text-[#FAF8F5]/50 text-xs">
                    {review.reviewerName} · {review.source}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
